import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiError, endpoints, type DJPick } from "../api";
import { useAuth } from "../auth";
import { Card, CardContent } from "../components/ui/card";
import { Spinner } from "../components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const MINT = "#aaffdc";
const CYAN = "#81ecff";
const PURPLE = "#c57eff";

export default function DjTargets() {
  const { slug } = useParams<{ slug: string }>();
  const { active } = useAuth();

  const release = useQuery({
    queryKey: ["release", active?.slug, slug],
    queryFn: () => endpoints.getRelease(active!.slug, slug!),
    enabled: !!active && !!slug,
  });

  // Auto-rank when the page loads. Ranking is deterministic and per-label
  // scoped, so the same release always returns the same picks — caching for
  // 30s avoids re-running on every back/forward.
  const pick = useQuery<{ picks: DJPick[] }, Error>({
    queryKey: ["dj-pick", active?.slug, slug],
    queryFn: () => endpoints.djPick(active!.slug, slug!),
    enabled: !!active && !!slug,
    staleTime: 30_000,
    retry: false,
  });

  if (release.isLoading) return <div className="p-6"><Spinner /></div>;
  if (!release.data) return null;
  const r = release.data.release;
  const err = pick.error instanceof ApiError ? pick.error : null;

  return (
    <div className="space-y-6 pb-12 px-8 pt-8">
      <div>
        <h1 className="text-foreground uppercase" style={{ fontSize: "20px", letterSpacing: "0.02em" }}>DJ targets</h1>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Ranking your DJ pool against {r.title} by BPM fit, genre overlap, and recent activity.
        </p>
      </div>

      {pick.isLoading && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center py-12">
              <Spinner />
            </div>
          </CardContent>
        </Card>
      )}

      {err && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{err.message}</p>
            {err.status === 400 && (
              <p className="text-xs text-muted-foreground mt-2">
                Import a DJ pool first — UI for this is coming.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {pick.data && (
        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px]">Score</TableHead>
                  <TableHead>DJ</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pick.data.picks.map((p) => (
                  <TableRow key={p.dj_id}>
                    <TableCell>
                      {p.unranked ? (
                        <span style={{ backgroundColor: "#262626", color: "#adaaaa", padding: "2px 8px", borderRadius: 4, fontSize: "0.65rem", fontWeight: 400, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                          unranked
                        </span>
                      ) : (
                        <span style={{ backgroundColor: "#262626", color: scoreColor(p.score), padding: "2px 8px", borderRadius: 4, fontSize: "0.75rem", fontWeight: 400 }} className="tabular-nums">
                          {p.score}<span style={{ opacity: 0.5 }}> / 100</span>
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-normal">{p.dj.name}</TableCell>
                    <TableCell className="text-sm" style={{ color: p.unranked ? "#adaaaa" : undefined }}>{p.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function scoreColor(score: number): string {
  if (score >= 85) return MINT;
  if (score >= 70) return CYAN;
  if (score >= 55) return PURPLE;
  return "#adaaaa";
}

